// ============================================================================
// src/api.js — the single point of contact with the backend.
// ============================================================================
// Every other file in this app MUST import from here, not from @supabase/supabase-js.
// Why: when we migrate from Supabase to AWS (RDS + Cognito + S3) down the road,
// only this file changes. React components stay identical.
//
// Pattern:  api.<resource>.<verb>(...)
//   api.tenants.list()
//   api.tenants.create({ name, rates, ... })
//   api.auth.current()
//   api.storage.upload(bucket, path, file)
// ============================================================================

import { createClient } from '@supabase/supabase-js';

// ---------- 1. Create the client ----------
// Vite exposes env vars via `import.meta.env`. Only VITE_-prefixed vars end up
// in the browser bundle — that's a safety rail.
const url     = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url || !anonKey) {
  // Fail loudly in dev instead of silently breaking when someone forgets to
  // copy .env.example to .env.local after cloning the repo.
  throw new Error(
    'Missing Supabase env vars. Copy .env.example to .env.local and fill in ' +
    'VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.'
  );
}

export const supabase = createClient(url, anonKey, {
  auth: {
    // Persist the login session across page reloads (localStorage).
    persistSession: true,
    // Refresh the JWT automatically ~5 min before it expires so users don't
    // get booted mid-session.
    autoRefreshToken: true,
    // When the app boots in a new tab, detect an existing session.
    detectSessionInUrl: true,
  },
});


// ---------- 2. Small utility ----------
// Every Supabase query returns { data, error }. We never want `data` with a
// silent error — that hides real problems. This helper throws on error so the
// caller can .catch() or let it bubble.
// Resolve the caller's active tenant id from session — used when an
// INSERT needs tenant_id but the call site doesn't pass one. Prefers the
// explicit user_active_tenant selection (multi-tenant users), falls back
// to the user's first membership.
async function resolveActiveTenantId() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data: active } = await supabase
    .from('user_active_tenant')
    .select('tenant_id')
    .eq('auth_sub', user.id)
    .maybeSingle();
  if (active?.tenant_id) return active.tenant_id;
  const { data: rows } = await supabase
    .from('users')
    .select('tenant_id')
    .eq('email', (user.email || '').toLowerCase())
    .limit(1);
  return rows?.[0]?.tenant_id || null;
}

function unwrap({ data, error }) {
  if (error) throw error;
  return data;
}


// ---------- 3. Shape mappers ----------
// SQL convention: snake_case (txn_mapping, created_at).
// JS convention: camelCase (txnMapping, createdAt).
// These two functions convert between the two so React components never see
// snake_case and SQL files never see camelCase.

// ---------- Customers / Runs / Invoices mappers ----------
function customerFromRow(r) {
  if (!r) return null;
  return {
    id: r.id, tenantId: r.tenant_id, name: r.name, rootId: r.root_id,
    branch: r.branch, repUserId: r.rep_user_id, since: r.since,
    firstInvoice: r.first_invoice, active: r.active,
    createdAt: r.created_at, updatedAt: r.updated_at,
  };
}
function customerToRow(c) {
  const out = {};
  if (c.tenantId !== undefined) out.tenant_id = c.tenantId;
  if (c.name !== undefined) out.name = c.name;
  if (c.rootId !== undefined) out.root_id = c.rootId;
  if (c.branch !== undefined) out.branch = c.branch;
  if (c.repUserId !== undefined) out.rep_user_id = c.repUserId;
  if (c.since !== undefined) out.since = c.since;
  if (c.firstInvoice !== undefined) out.first_invoice = c.firstInvoice;
  if (c.active !== undefined) out.active = c.active;
  return out;
}

function runFromRow(r) {
  if (!r) return null;
  return {
    id: r.id, tenantId: r.tenant_id, period: r.period,
    periodStart: r.period_start, periodEnd: r.period_end,
    status: r.status, rateSnapshot: r.rate_snapshot, totals: r.totals,
    createdBy: r.created_by,
    approvedAt: r.approved_at, approvedBy: r.approved_by,
    finalizedAt: r.finalized_at, finalizedBy: r.finalized_by,
    rejectionReason: r.rejection_reason,
    visibleToReps: r.visible_to_reps !== false, // default true if NULL
    // AR-doc format detected/pinned at run creation. Null on old runs =>
    // fall back to the tenant setting when building match keys.
    arDocumentFormat: r.ar_document_format ?? null,
    createdAt: r.created_at, updatedAt: r.updated_at,
  };
}
function runToRow(r) {
  const out = {};
  if (r.tenantId !== undefined) out.tenant_id = r.tenantId;
  if (r.period !== undefined) out.period = r.period;
  if (r.periodStart !== undefined) out.period_start = r.periodStart;
  if (r.periodEnd !== undefined) out.period_end = r.periodEnd;
  if (r.status !== undefined) out.status = r.status;
  if (r.rateSnapshot !== undefined) out.rate_snapshot = r.rateSnapshot;
  if (r.totals !== undefined) out.totals = r.totals;
  if (r.createdBy !== undefined) out.created_by = r.createdBy;
  if (r.approvedAt !== undefined) out.approved_at = r.approvedAt;
  if (r.approvedBy !== undefined) out.approved_by = r.approvedBy;
  if (r.finalizedAt !== undefined) out.finalized_at = r.finalizedAt;
  if (r.finalizedBy !== undefined) out.finalized_by = r.finalizedBy;
  if (r.rejectionReason !== undefined) out.rejection_reason = r.rejectionReason;
  if (r.visibleToReps !== undefined) out.visible_to_reps = !!r.visibleToReps;
  if (r.arDocumentFormat !== undefined) out.ar_document_format = r.arDocumentFormat;
  return out;
}

function invoiceFromRow(r) {
  if (!r) return null;
  return {
    id: r.id, tenantId: r.tenant_id, runId: r.run_id,
    customerId: r.customer_id, repUserId: r.rep_user_id,
    invoiceNumber: r.invoice_number, branch: r.branch, entity: r.entity,
    invoiceDate: r.invoice_date,
    totalBill: Number(r.total_bill), grossWages: Number(r.gross_wages),
    grossProfit: Number(r.gross_profit), aca: Number(r.aca), sick: Number(r.sick),
    net: Number(r.net), tier: r.tier, rate: r.rate == null ? null : Number(r.rate),
    commission: Number(r.commission),
    originalCommission: r.original_commission == null ? null : Number(r.original_commission),
    overridden: r.overridden, overrideReason: r.override_reason,
    excluded: r.excluded, paid: r.paid,
    outstanding: Number(r.outstanding), ageDays: r.age_days,
    // Approver-level rejection (migration 0015). Distinct from `excluded`,
    // which an admin can toggle pre-approval; rejection is the approver's
    // call after the run is finalized.
    rejected: !!r.rejected,
    rejectionReason: r.rejection_reason,
    rejectedByUserId: r.rejected_by_user_id,
    rejectedAt: r.rejected_at,
    // Cash-basis "held" — set by calculate() when plan.paymentBasis='cash'
    // and the invoice is unpaid at calculate time.
    heldForCollection: !!r.held_for_collection,
    // Carryover plumbing (migration 0019) — see applyCarryoversForRun.
    isCarryover: !!r.is_carryover,
    sourceInvoiceId: r.source_invoice_id || null,
    sourceRunId: r.source_run_id || null,
    carriedOver: !!r.carried_over,
    createdAt: r.created_at, updatedAt: r.updated_at,
  };
}
function invoiceToRow(i) {
  const num = (v) => (v === undefined || v === null ? undefined : Number(v));
  const out = {};
  if (i.tenantId !== undefined) out.tenant_id = i.tenantId;
  if (i.runId !== undefined) out.run_id = i.runId;
  if (i.customerId !== undefined) out.customer_id = i.customerId;
  if (i.repUserId !== undefined) out.rep_user_id = i.repUserId;
  if (i.invoiceNumber !== undefined) out.invoice_number = i.invoiceNumber;
  if (i.branch !== undefined) out.branch = i.branch;
  if (i.entity !== undefined) out.entity = i.entity;
  if (i.invoiceDate !== undefined) out.invoice_date = i.invoiceDate;
  if (i.totalBill !== undefined) out.total_bill = num(i.totalBill);
  if (i.grossWages !== undefined) out.gross_wages = num(i.grossWages);
  if (i.grossProfit !== undefined) out.gross_profit = num(i.grossProfit);
  if (i.aca !== undefined) out.aca = num(i.aca);
  if (i.sick !== undefined) out.sick = num(i.sick);
  if (i.net !== undefined) out.net = num(i.net);
  if (i.tier !== undefined) out.tier = i.tier;
  if (i.rate !== undefined) out.rate = num(i.rate);
  if (i.commission !== undefined) out.commission = num(i.commission);
  if (i.originalCommission !== undefined) out.original_commission = num(i.originalCommission);
  if (i.overridden !== undefined) out.overridden = i.overridden;
  if (i.overrideReason !== undefined) out.override_reason = i.overrideReason;
  if (i.excluded !== undefined) out.excluded = i.excluded;
  if (i.paid !== undefined) out.paid = i.paid;
  if (i.outstanding !== undefined) out.outstanding = num(i.outstanding);
  if (i.ageDays !== undefined) out.age_days = i.ageDays;
  if (i.rejected !== undefined) out.rejected = i.rejected;
  if (i.rejectionReason !== undefined) out.rejection_reason = i.rejectionReason;
  if (i.rejectedByUserId !== undefined) out.rejected_by_user_id = i.rejectedByUserId;
  if (i.rejectedAt !== undefined) out.rejected_at = i.rejectedAt;
  if (i.heldForCollection !== undefined) out.held_for_collection = i.heldForCollection;
  if (i.isCarryover !== undefined) out.is_carryover = i.isCarryover;
  if (i.sourceInvoiceId !== undefined) out.source_invoice_id = i.sourceInvoiceId;
  if (i.sourceRunId !== undefined) out.source_run_id = i.sourceRunId;
  if (i.carriedOver !== undefined) out.carried_over = i.carriedOver;
  return out;
}

function tenantFromRow(row) {
  if (!row) return null;
  return {
    id: row.id,
    name: row.name,
    entities: row.entities ?? [],
    rates: row.rates ?? {},
    txnMapping: row.txn_mapping ?? {},
    arMapping: row.ar_mapping ?? {},
    arDocumentFormat: row.ar_document_format,
    // Optional per-tenant GP normalization. When set (e.g. 0.105), the
    // txn parser overwrites GP as override × TotalBill so downstream
    // commission math stays consistent regardless of what the source
    // system exported. Null = use the file's raw GP as-is.
    grossProfitOverride: row.gross_profit_override == null ? null : Number(row.gross_profit_override),
    logoUrl: row.logo_url || null,
    branchCodes: row.branch_codes ?? {},
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}
function userFromRow(row) {
  if (!row) return null;
  return {
    id: row.id,
    tenantId: row.tenant_id,
    authSub: row.auth_sub,
    email: row.email,
    name: row.name,
    role: row.role,
    repId: row.rep_id,
    active: row.active,
    y1Rate: row.y1_rate == null ? null : Number(row.y1_rate),
    y2Rate: row.y2_rate == null ? null : Number(row.y2_rate),
    commissionPlanId: row.commission_plan_id || null,
    paymentBasis: row.payment_basis || null,
    roleId: row.role_id || null,
    archived: !!row.archived,
    archivedAt: row.archived_at,
    archivedByUserId: row.archived_by_user_id,
    staged: !!row.staged,
    invitedAt: row.invited_at,
    platformAdmin: !!row.platform_admin,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}
function userToRow(u) {
  const out = {};
  if (u.tenantId !== undefined) out.tenant_id = u.tenantId;
  if (u.authSub  !== undefined) out.auth_sub  = u.authSub;
  if (u.email    !== undefined) out.email     = u.email.toLowerCase();
  if (u.name     !== undefined) out.name      = u.name;
  if (u.role     !== undefined) out.role      = u.role;
  if (u.repId    !== undefined) out.rep_id    = u.repId;
  if (u.active   !== undefined) out.active    = u.active;
  if (u.y1Rate   !== undefined) out.y1_rate   = u.y1Rate === '' || u.y1Rate == null ? null : Number(u.y1Rate);
  if (u.y2Rate   !== undefined) out.y2_rate   = u.y2Rate === '' || u.y2Rate == null ? null : Number(u.y2Rate);
  if (u.commissionPlanId !== undefined) out.commission_plan_id = u.commissionPlanId || null;
  if (u.paymentBasis !== undefined) out.payment_basis = u.paymentBasis || null;
  if (u.roleId !== undefined) out.role_id = u.roleId || null;
  if (u.archived !== undefined) out.archived = u.archived;
  if (u.archivedAt !== undefined) out.archived_at = u.archivedAt;
  if (u.archivedByUserId !== undefined) out.archived_by_user_id = u.archivedByUserId;
  if (u.staged !== undefined) out.staged = u.staged;
  if (u.invitedAt !== undefined) out.invited_at = u.invitedAt;
  return out;
}

// ---- User-role mappers ----
function roleFromRow(r) {
  if (!r) return null;
  return {
    id: r.id,
    tenantId: r.tenant_id,
    name: r.name,
    description: r.description,
    baseRole: r.base_role,
    permissions: r.permissions || {},
    isSystemRole: !!r.is_system_role,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}
function roleToRow(r) {
  const out = {};
  if (r.tenantId    !== undefined) out.tenant_id   = r.tenantId;
  if (r.name        !== undefined) out.name        = r.name;
  if (r.description !== undefined) out.description = r.description;
  if (r.baseRole    !== undefined) out.base_role   = r.baseRole;
  if (r.permissions !== undefined) out.permissions = r.permissions ?? {};
  // is_system_role is set at seed time and not editable from the app.
  return out;
}

// ---- Commission plan mappers ----
function planFromRow(r) {
  if (!r) return null;
  return {
    id:         r.id,
    tenantId:   r.tenant_id,
    name:       r.name,
    isDefault:  r.is_default,
    baseField:  r.base_field,
    tierBasis:  r.tier_basis,
    tiers:      r.tiers ?? [],
    deductions: r.deductions ?? [],
    minQuota:   r.min_quota == null ? 0 : Number(r.min_quota),
    quotaMode:  r.quota_mode,
    paymentBasis: r.payment_basis || 'accrual',
    createdAt:  r.created_at,
    updatedAt:  r.updated_at,
  };
}
function planToRow(p) {
  const out = {};
  if (p.tenantId     !== undefined) out.tenant_id     = p.tenantId;
  if (p.name         !== undefined) out.name          = p.name;
  if (p.isDefault    !== undefined) out.is_default    = !!p.isDefault;
  if (p.baseField    !== undefined) out.base_field    = p.baseField;
  if (p.tierBasis    !== undefined) out.tier_basis    = p.tierBasis;
  if (p.tiers        !== undefined) out.tiers         = p.tiers ?? [];
  if (p.deductions   !== undefined) out.deductions    = p.deductions ?? [];
  if (p.minQuota     !== undefined) out.min_quota     = Number(p.minQuota || 0);
  if (p.quotaMode    !== undefined) out.quota_mode    = p.quotaMode;
  if (p.paymentBasis !== undefined) out.payment_basis = p.paymentBasis;
  return out;
}

function tenantToRow(t) {
  // Only include fields that were explicitly set so partial patches (update)
  // don't wipe out unrelated columns.
  const out = {};
  if (t.name            !== undefined) out.name               = t.name;
  if (t.entities        !== undefined) out.entities           = t.entities ?? [];
  if (t.rates           !== undefined) out.rates              = t.rates ?? {};
  if (t.txnMapping      !== undefined) out.txn_mapping        = t.txnMapping ?? {};
  if (t.arMapping       !== undefined) out.ar_mapping         = t.arMapping ?? {};
  if (t.arDocumentFormat!== undefined) out.ar_document_format = t.arDocumentFormat ?? 'branchInitials+invoiceNumber';
  if (t.grossProfitOverride !== undefined) out.gross_profit_override = t.grossProfitOverride === '' ? null : t.grossProfitOverride;
  if (t.logoUrl         !== undefined) out.logo_url           = t.logoUrl;
  if (t.branchCodes     !== undefined) out.branch_codes       = t.branchCodes ?? {};
  return out;
}


// ---------- 4. Domain APIs ----------
// Each sub-object wraps one logical resource. Components call these — they
// never write raw .from('tenants') queries.

export const api = {
  // -------------------- tenants --------------------
  tenants: {
    /** List all tenants the current user can see (RLS decides). */
    list: () => supabase
      .from('tenants')
      .select('*')
      .order('created_at', { ascending: true })
      .then(unwrap)
      .then(rows => rows.map(tenantFromRow)),

    /** Get a single tenant by id. Returns null if not found / not visible. */
    get: (id) => supabase
      .from('tenants')
      .select('*')
      .eq('id', id)
      .maybeSingle()
      .then(unwrap)
      .then(tenantFromRow),

    /** Create a new tenant. Returns the row (with server-assigned id, timestamps). */
    create: (tenant) => supabase
      .from('tenants')
      .insert(tenantToRow(tenant))
      .select()
      .single()
      .then(unwrap)
      .then(tenantFromRow),

    /** Update an existing tenant. `patch` is a partial camelCase object. */
    update: (id, patch) => supabase
      .from('tenants')
      .update(tenantToRow(patch))
      .eq('id', id)
      .select()
      .single()
      .then(unwrap)
      .then(tenantFromRow),
  },

  // -------------------- user_active_tenant (multi-tenant context) --------------------
  // Tracks which tenant the auth user is currently "in" — used by
  // platform admins / future multi-tenant members. The RLS helper
  // current_tenant_id() reads from this table first.
  userActiveTenant: {
    /** Get the current active tenant id for the signed-in auth user. */
    get: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      const { data, error } = await supabase
        .from('user_active_tenant')
        .select('tenant_id')
        .eq('auth_sub', user.id)
        .maybeSingle();
      if (error) { console.warn('user_active_tenant get:', error); return null; }
      return data?.tenant_id || null;
    },
    /** Set the active tenant. Upserts on auth_sub. */
    set: async (tenantId) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not signed in');
      const { error } = await supabase
        .from('user_active_tenant')
        .upsert({ auth_sub: user.id, tenant_id: tenantId, updated_at: new Date().toISOString() });
      if (error) throw error;
    },
  },

  // -------------------- auth --------------------
  auth: {
    /** The currently-signed-in auth user (from Supabase Auth), or null. */
    current: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
    /** Subscribe to sign-in / sign-out events. Returns an unsubscribe function. */
    onChange: (cb) => {
      const { data: { subscription } } = supabase.auth.onAuthStateChange((_evt, session) => cb(session?.user ?? null));
      return () => subscription.unsubscribe();
    },
    /** Start a magic-link sign-in — sends an email with a one-time login URL. */
    signInWithOtp: (email) => supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin },
    }).then(({ error }) => { if (error) throw error; }),
    /** Sign in with email + password. */
    signInWithPassword: (email, password) => supabase.auth.signInWithPassword({
      email: email.toLowerCase().trim(),
      password,
    }).then(({ error }) => { if (error) throw error; }),
    /** Send a password reset email (rate-limited by Supabase). */
    resetPassword: (email) => supabase.auth.resetPasswordForEmail(email.toLowerCase().trim(), {
      redirectTo: window.location.origin + '/?type=recovery',
    }).then(({ error }) => { if (error) throw error; }),
    /** Update the current user's password. Used during the recovery flow. */
    updatePassword: (newPassword) => supabase.auth.updateUser({
      password: newPassword,
    }).then(({ error }) => { if (error) throw error; }),
    /** Sign out the current user. */
    signOut: () => supabase.auth.signOut(),
  },

  // -------------------- users --------------------
  users: {
    /** List all users visible to the caller (RLS decides: admins see tenant, reps see self). */
    list: () => supabase
      .from('users')
      .select('*')
      .order('created_at', { ascending: true })
      .then(unwrap)
      .then(rows => rows.map(userFromRow)),

    /** Find the app user whose email matches — used right after magic-link sign-in.
     *  After 0038 a single email can match rows in multiple tenants
     *  (platform admins). We return the row whose tenant_id matches the
     *  current_tenant_id() helper, which itself reads from
     *  user_active_tenant first. RLS filters the SELECT to rows the
     *  caller can read, so this is safe to grab as a list. */
    getByEmail: async (email) => {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('email', email.toLowerCase());
      if (error) throw error;
      if (!data || data.length === 0) return null;
      if (data.length === 1) return userFromRow(data[0]);
      // Multi-tenant case — pick the row whose tenant matches the active selection.
      const { data: active } = await supabase
        .from('user_active_tenant')
        .select('tenant_id')
        .maybeSingle();
      const activeTid = active?.tenant_id;
      const preferred = activeTid && data.find(r => r.tenant_id === activeTid);
      return userFromRow(preferred || data[0]);
    },

    /** First-login: stamp auth_sub onto the row so future requests identify this user. */
    claimAuthSub: (id, authSub) => supabase
      .from('users')
      .update({ auth_sub: authSub })
      .eq('id', id)
      .select()
      .single()
      .then(unwrap)
      .then(userFromRow),

    /** Admin action — add a new user (rep or admin) to the current tenant. */
    create: (user) => supabase
      .from('users')
      .insert(userToRow(user))
      .select()
      .single()
      .then(unwrap)
      .then(userFromRow),

    update: (id, patch) => supabase
      .from('users')
      .update(userToRow(patch))
      .eq('id', id)
      .select()
      .single()
      .then(unwrap)
      .then(userFromRow),

    /**
     * Archive a deactivated user — removes them from the working roster but
     * keeps the row for audit. CHECK constraint at DB layer requires the
     * user to be inactive first, so callers should toggle active=false
     * before archiving (or pass it together).
     */
    archive: (id, byUserId) => supabase
      .from('users')
      .update({
        active: false,
        archived: true,
        archived_at: new Date().toISOString(),
        archived_by_user_id: byUserId,
      })
      .eq('id', id)
      .select()
      .single()
      .then(unwrap)
      .then(userFromRow),

    /** Restore an archived user. Returns them to deactivated (not auto-active). */
    unarchive: (id) => supabase
      .from('users')
      .update({
        archived: false,
        archived_at: null,
        archived_by_user_id: null,
      })
      .eq('id', id)
      .select()
      .single()
      .then(unwrap)
      .then(userFromRow),

    /**
     * Admin-only: invite a new user (creates auth user + app row in one step).
     * Returns { user, tempPassword }. The admin shares the temp password with the
     * new user via their own channel — the edge function never emails it.
     */
    invite: async ({ email, name, role, repId }) => {
      const { data, error } = await supabase.functions.invoke('invite-user', {
        body: { email, name, role, repId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return { user: userFromRow(data.user), tempPassword: data.tempPassword, message: data.message };
    },

    /**
     * Stage a user — create the public.users row WITHOUT creating an auth
     * account. The user can be referenced (assigned customers, given a
     * commission plan, previewed via "View as") but cannot sign in. The
     * admin clicks "Send invitation" later to convert to a real user.
     *
     * RLS allows admins to insert into users for their own tenant — we
     * don't need the edge function for the row-only path.
     */
    stage: async ({ tenantId, email, name, role, roleId, repId, y1Rate, y2Rate, commissionPlanId, paymentBasis }) => {
      // Resolve tenant_id from session if not provided. RLS on
      // public.users.INSERT requires tenant_id = current_tenant_id().
      let tid = tenantId;
      if (!tid) tid = await resolveActiveTenantId();
      if (!tid) throw new Error('Could not resolve your tenant — try refreshing and signing in again.');
      const { data, error } = await supabase
        .from('users')
        .insert({
          tenant_id: tid,
          email: (email || '').toLowerCase().trim(),
          name: (name || '').trim(),
          role,
          role_id: roleId || null,
          rep_id: role === 'rep' ? (repId || null) : null,
          active: true,
          staged: true,
          y1_rate: y1Rate ?? null,
          y2_rate: y2Rate ?? null,
          commission_plan_id: commissionPlanId || null,
          payment_basis: paymentBasis || null,
        })
        .select()
        .single();
      if (error) throw error;
      return userFromRow(data);
    },

    /**
     * Promote a staged user — call the edge function with existingUserId
     * so it creates the auth account and links it to the existing row.
     */
    sendInvitation: async (user) => {
      if (!user.staged) throw new Error('User is already invited.');
      const { data, error } = await supabase.functions.invoke('invite-user', {
        body: {
          email: user.email,
          name: user.name,
          role: user.role,
          repId: user.repId || null,
          existingUserId: user.id,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return { user: userFromRow(data.user), tempPassword: data.tempPassword, message: data.message };
    },
  },

  // -------------------- customers --------------------
  customers: {
    list: () => supabase.from('customers').select('*').order('name')
      .then(unwrap).then(rs => rs.map(customerFromRow)),
    create: (c) => supabase.from('customers').insert(customerToRow(c)).select().single()
      .then(unwrap).then(customerFromRow),
    update: (id, patch) => supabase.from('customers').update(customerToRow(patch)).eq('id', id).select().single()
      .then(unwrap).then(customerFromRow),
    /** Bulk-upsert: used by the upload wizard to create/update customers parsed from a Transaction file. */
    upsertMany: (rows) => supabase.from('customers')
      .upsert(rows.map(customerToRow), { onConflict: 'tenant_id,root_id' }).select()
      .then(unwrap).then(rs => rs.map(customerFromRow)),
  },

  // -------------------- runs --------------------
  runs: {
    list: () => supabase.from('runs').select('*').order('period_start', { ascending: false })
      .then(unwrap).then(rs => rs.map(runFromRow)),
    get: (id) => supabase.from('runs').select('*').eq('id', id).maybeSingle()
      .then(unwrap).then(runFromRow),
    create: (r) => supabase.from('runs').insert(runToRow(r)).select().single()
      .then(unwrap).then(runFromRow),
    update: (id, patch) => supabase.from('runs').update(runToRow(patch)).eq('id', id).select().single()
      .then(unwrap).then(runFromRow),

    /** Approve a draft run. Caller must be an admin AND not the run's creator. */
    approve: (id, approverUserId) => supabase.from('runs')
      .update({ status: 'approved', approved_at: new Date().toISOString(), approved_by: approverUserId, rejection_reason: null })
      .eq('id', id).select().single().then(unwrap).then(runFromRow),

    /** Send an approved run back to draft with an audit reason. */
    unapprove: (id, reason) => supabase.from('runs')
      .update({ status: 'draft', approved_at: null, approved_by: null, rejection_reason: reason })
      .eq('id', id).select().single().then(unwrap).then(runFromRow),

    /** Delete a draft run. RLS enforces admin + status='draft'. Cascade
     *  drops every invoice in the run automatically. */
    delete: (id) => supabase.from('runs').delete().eq('id', id)
      .then(({ error }) => { if (error) throw error; }),
  },

  // -------------------- commission plans --------------------
  // -------------------- user roles (RBAC) --------------------
  // Per-tenant named permission bundles (Administrator, Sales Rep, Approver,
  // Sales Manager, plus any custom roles the admin creates). System roles
  // can't be edited or deleted; both UI and RLS enforce.
  roles: {
    list: () => supabase.from('user_roles').select('*')
      .order('is_system_role', { ascending: false })
      .order('name')
      .then(unwrap).then(rs => rs.map(roleFromRow)),
    get: (id) => supabase.from('user_roles').select('*').eq('id', id).maybeSingle()
      .then(unwrap).then(roleFromRow),
    create: (r) => supabase.from('user_roles').insert(roleToRow(r)).select().single()
      .then(unwrap).then(roleFromRow),
    update: (id, patch) => supabase.from('user_roles').update(roleToRow(patch)).eq('id', id).select().single()
      .then(unwrap).then(roleFromRow),
    delete: (id) => supabase.from('user_roles').delete().eq('id', id)
      .then(({ error }) => { if (error) throw error; }),
  },

  commissionPlans: {
    list: () => supabase.from('commission_plans').select('*').order('is_default', { ascending: false }).order('name')
      .then(unwrap).then(rs => rs.map(planFromRow)),
    get: (id) => supabase.from('commission_plans').select('*').eq('id', id).maybeSingle()
      .then(unwrap).then(planFromRow),
    /**
     * Create a plan. Defends against the RLS-by-tenant-id trap by:
     *   1. Resolving tid from session (user_active_tenant -> users).
     *   2. Persisting tid to user_active_tenant via upsert — so the
     *      Postgres helper current_tenant_id() returns the same value
     *      we're trying to insert. Without this step, an admin whose
     *      user_active_tenant row is missing or stale gets
     *      'new row violates row-level security policy' even with a
     *      tenant_id on the row.
     *   3. Inserting with the resolved tid.
     */
    create: async (p) => {
      const { data: { user } } = await supabase.auth.getUser();
      let tid = p.tenantId;
      if (!tid) tid = await resolveActiveTenantId();
      if (!tid) throw new Error('Could not resolve tenant — try refreshing.');
      // Sync the session's active tenant so RLS sees the same value.
      if (user) {
        try {
          await supabase.from('user_active_tenant').upsert(
            { auth_sub: user.id, tenant_id: tid, updated_at: new Date().toISOString() }
          );
        } catch (e) { console.warn('user_active_tenant sync (non-fatal):', e); }
      }
      const { data, error } = await supabase
        .from('commission_plans')
        .insert(planToRow({ ...p, tenantId: tid }))
        .select().single();
      if (error) throw error;
      return planFromRow(data);
    },
    update: (id, patch) => supabase.from('commission_plans').update(planToRow(patch)).eq('id', id).select().single()
      .then(unwrap).then(planFromRow),
    delete: (id) => supabase.from('commission_plans').delete().eq('id', id)
      .then(({ error }) => { if (error) throw error; }),
  },

  // -------------------- invoices --------------------
  invoices: {
    listByRun: (runId) => supabase.from('invoices').select('*').eq('run_id', runId).order('invoice_date')
      .then(unwrap).then(rs => rs.map(invoiceFromRow)),
    listForRep: (runId, repUserId) => supabase.from('invoices')
      .select('*').eq('run_id', runId).eq('rep_user_id', repUserId).order('invoice_date')
      .then(unwrap).then(rs => rs.map(invoiceFromRow)),
    /** All invoices for the currently-authenticated rep (RLS filters for us). */
    listAllForCurrentRep: () => supabase.from('invoices').select('*').order('invoice_date', { ascending: false })
      .then(unwrap).then(rs => rs.map(invoiceFromRow)),
    /** Bulk insert — used by the upload wizard once the files are parsed. */
    insertMany: (rows) => supabase.from('invoices').insert(rows.map(invoiceToRow)).select()
      .then(unwrap).then(rs => rs.map(invoiceFromRow)),
    update: (id, patch) => supabase.from('invoices').update(invoiceToRow(patch)).eq('id', id).select().single()
      .then(unwrap).then(invoiceFromRow),

    /**
     * Approver action — reject a single invoice with a reason. The rep
     * earns $0 commission on rejected invoices. Caller is responsible for
     * the segregation-of-duties check (approver != run creator) — same
     * pattern as `runs.approve`. RLS already restricts updates to admins
     * of the tenant, so a rep can't call this even if they tried.
     */
    reject: (id, reason, byUserId) => supabase.from('invoices').update({
      rejected: true,
      rejection_reason: reason,
      rejected_by_user_id: byUserId,
      rejected_at: new Date().toISOString(),
    }).eq('id', id).select().single().then(unwrap).then(invoiceFromRow),

    /**
     * Carryover plumbing — list held invoices in PRIOR runs that haven't
     * been carried over yet. Used by the AR-upload step to figure out
     * which previously-unpaid invoices are now paid (because they're
     * absent from the current AR), so we can carry them into the
     * current run as commission earned this period.
     */
    listOutstandingHeld: (excludeRunId) => supabase.from('invoices')
      .select('*')
      .eq('held_for_collection', true)
      .eq('carried_over', false)
      .neq('run_id', excludeRunId)
      .then(unwrap).then(rs => rs.map(invoiceFromRow)),

    /** Clear an existing rejection (e.g. approver changed their mind before the run is approved). */
    unreject: (id) => supabase.from('invoices').update({
      rejected: false,
      rejection_reason: null,
      rejected_by_user_id: null,
      rejected_at: null,
    }).eq('id', id).select().single().then(unwrap).then(invoiceFromRow),

    /** Clear + replace all invoices for a draft run (re-upload / recalculate). */
    replaceForRun: async (runId, rows) => {
      const { error: delErr } = await supabase.from('invoices').delete().eq('run_id', runId);
      if (delErr) throw delErr;
      if (rows.length === 0) return [];
      return supabase.from('invoices').insert(rows.map(invoiceToRow)).select()
        .then(unwrap).then(rs => rs.map(invoiceFromRow));
    },
  },

  // -------------------- storage (filled in later) --------------------
  storage: {
    upload: (bucket, path, file) => supabase.storage.from(bucket).upload(path, file),
    signedUrl: (bucket, path, ttlSeconds = 3600) =>
      supabase.storage.from(bucket).createSignedUrl(path, ttlSeconds).then(unwrap),
  },
};
